/* ============================================================================
   SHIOK ALIVE  ·  motion runtime
   ----------------------------------------------------------------------------
   Vanilla, no dependencies, ~5KB. Everything is opt-in through data attributes
   so a section that does not use motion pays nothing.

   Rules this file obeys:
   - only transform and opacity are animated, never layout properties
   - one shared IntersectionObserver, one shared passive scroll listener
   - prefers-reduced-motion switches motion off and reveals all content
   - inside the Shopify theme editor everything is revealed immediately
   ========================================================================= */
(function () {
  'use strict';

  /* the loader snippet may appear more than once on a page */
  if (window.__shiokAlive) return;
  window.__shiokAlive = true;

  var docEl = document.documentElement;
  docEl.classList.add('sh-js');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function isReduced() { return reduce.matches; }

  /* ------------------------------------------------------------------ */
  /* 1. Scroll reveal                                                    */
  /* ------------------------------------------------------------------ */
  var revealObserver = null;

  function revealAll(root) {
    (root || document).querySelectorAll('[data-sh-reveal]').forEach(function (el) {
      el.classList.add('sh-in');
    });
  }

  function initReveal(root) {
    var scope = root || document;
    var items = scope.querySelectorAll('[data-sh-reveal]:not(.sh-observed)');
    if (!items.length) return;

    if (isReduced() || designMode || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('sh-observed', 'sh-in'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('sh-in');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }

    items.forEach(function (el) {
      el.classList.add('sh-observed');
      revealObserver.observe(el);
    });

    /* automatic stagger for groups that ask for it */
    scope.querySelectorAll('[data-sh-stagger]:not(.sh-staggered)').forEach(function (group) {
      group.classList.add('sh-staggered');
      var step = parseInt(group.getAttribute('data-sh-stagger'), 10);
      if (isNaN(step)) step = 90;
      var kids = group.querySelectorAll(':scope > [data-sh-reveal]');
      for (var i = 0; i < kids.length; i++) {
        if (!kids[i].style.getPropertyValue('--sh-delay')) {
          kids[i].style.setProperty('--sh-delay', (i * step) + 'ms');
        }
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* 2. Parallax and slow zoom                                           */
  /* ------------------------------------------------------------------ */
  var parallaxItems = [];
  var ticking = false;

  function collectParallax(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-sh-parallax]:not(.sh-px-bound)').forEach(function (el) {
      el.classList.add('sh-px-bound', 'sh-parallax-media');
      var strength = parseFloat(el.getAttribute('data-sh-parallax'));
      if (isNaN(strength)) strength = 12;
      /* 0 to 100 in the theme editor maps to a gentle 0 to 0.20 shift */
      var zoom = parseFloat(el.getAttribute('data-sh-zoom'));
      el.__shStrength = Math.max(0, Math.min(100, strength)) / 500;
      el.__shZoom = isNaN(zoom) ? 1.08 : zoom;
      el.style.setProperty('--sh-pscale', el.__shZoom);
      parallaxItems.push(el);
    });
  }

  function updateParallax() {
    ticking = false;
    if (!parallaxItems.length) return;
    var vh = window.innerHeight || docEl.clientHeight;
    for (var i = 0; i < parallaxItems.length; i++) {
      var el = parallaxItems[i];
      var rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) continue;
      var centre = rect.top + rect.height / 2;
      var offset = (centre - vh / 2) * el.__shStrength;
      /* never let the shift exceed the slack created by the scale */
      var slack = (rect.height * (el.__shZoom - 1)) / 2;
      if (slack > 0) offset = Math.max(-slack, Math.min(slack, offset));
      el.style.setProperty('--sh-py', offset.toFixed(2) + 'px');
    }
  }

  function requestParallax() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateParallax);
  }

  function initParallax(root) {
    if (isReduced()) return;
    collectParallax(root);
    if (parallaxItems.length) updateParallax();
  }

  /* ------------------------------------------------------------------ */
  /* 3. Sticky promo bar, and the theme's cloned sticky header           */
  /* ------------------------------------------------------------------ */
  function measurePromoBar() {
    var bar = document.querySelector('[data-sh-promobar][data-sh-sticky="true"]');
    if (!bar) {
      docEl.style.setProperty('--sh-promobar-h', '0px');
      document.body.classList.remove('sh-has-promobar');
      return;
    }
    document.body.classList.add('sh-has-promobar');
    docEl.style.setProperty('--sh-promobar-h', Math.round(bar.getBoundingClientRect().height) + 'px');
  }

  /* the theme appends body > .sticky-header late, so watch for it once */
  function watchStickyHeader() {
    if (!('MutationObserver' in window)) return;
    if (document.querySelector('.sticky-header')) { measurePromoBar(); return; }
    var mo = new MutationObserver(function () {
      if (document.querySelector('.sticky-header')) {
        measurePromoBar();
        mo.disconnect();
      }
    });
    mo.observe(document.body, { childList: true });
    window.setTimeout(function () { mo.disconnect(); }, 8000);
  }

  /* ------------------------------------------------------------------ */
  /* 4. Draggable rails (reviews, product rails)                         */
  /* ------------------------------------------------------------------ */
  function initRails(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-sh-drag]:not(.sh-drag-bound)').forEach(function (rail) {
      rail.classList.add('sh-drag-bound');
      var down = false, moved = false, startX = 0, startScroll = 0;

      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return; /* let the browser do native touch */
        down = true; moved = false;
        startX = e.clientX;
        startScroll = rail.scrollLeft;
      });
      rail.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) {
          if (!moved) { moved = true; rail.classList.add('sh-dragging'); }
          rail.scrollLeft = startScroll - dx;
        }
      });
      function release() {
        down = false;
        window.setTimeout(function () { rail.classList.remove('sh-dragging'); }, 0);
      }
      rail.addEventListener('pointerup', release);
      rail.addEventListener('pointercancel', release);
      rail.addEventListener('pointerleave', release);
      rail.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    });

    /* previous / next buttons */
    scope.querySelectorAll('[data-sh-rail-nav]:not(.sh-nav-bound)').forEach(function (btn) {
      btn.classList.add('sh-nav-bound');
      btn.addEventListener('click', function () {
        var target = document.getElementById(btn.getAttribute('data-sh-rail-target'));
        if (!target) return;
        var dir = btn.getAttribute('data-sh-rail-nav') === 'prev' ? -1 : 1;
        var first = target.firstElementChild;
        var step = first ? first.getBoundingClientRect().width + 24 : target.clientWidth * 0.8;
        target.scrollBy({
          left: dir * step,
          behavior: isReduced() ? 'auto' : 'smooth'
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* 5. Boot                                                             */
  /* ------------------------------------------------------------------ */
  function boot(root) {
    initReveal(root);
    initParallax(root);
    initRails(root);
    measurePromoBar();
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    boot(document);
    watchStickyHeader();
    window.addEventListener('scroll', requestParallax, { passive: true });
    window.addEventListener('resize', function () {
      measurePromoBar();
      requestParallax();
    }, { passive: true });
    window.addEventListener('load', function () {
      measurePromoBar();
      requestParallax();
    });
  });

  /* react if the visitor flips the reduced motion switch mid session */
  var onReduceChange = function () {
    if (isReduced()) {
      revealAll(document);
      parallaxItems.forEach(function (el) { el.style.setProperty('--sh-py', '0px'); });
    } else {
      initParallax(document);
    }
  };
  if (reduce.addEventListener) reduce.addEventListener('change', onReduceChange);
  else if (reduce.addListener) reduce.addListener(onReduceChange);

  /* theme editor: sections are re-rendered on every change */
  document.addEventListener('shopify:section:load', function (e) {
    parallaxItems = parallaxItems.filter(function (el) { return document.contains(el); });
    boot(e.target);
    revealAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function () {
    parallaxItems = parallaxItems.filter(function (el) { return document.contains(el); });
  });
  document.addEventListener('shopify:block:select', function (e) {
    revealAll(e.target);
  });

  window.ShiokAlive = {
    refresh: function (root) { boot(root || document); },
    revealAll: revealAll
  };
})();